import { transformItemImgUrl } from '@app/shared/utils/img-url';

import { FINAL_LEVEL_ID } from '../event.constants';
import type { AprilMadnessEventModel } from '../state/april-madness-event.model';
import type { ICurrentLevelQuest, ICurrentLevelQuestLevel } from '../types/backend';
import type { ILevelQuestUserInfo, ILevelQuestUserLevel, ILevelQuestUserLevelUpdate } from '../types/backend/';
import type { ECaseType, IEventLevel, ILastItem } from '../types/frontend/';
import { EAprilMadnessEventLevelStatus } from '../types/frontend/';

type EventStateAdapterResult = Pick<
  AprilMadnessEventModel,
  | 'endAt'
  | 'currentStep'
  | 'nextLevelRequiredExp'
  | 'currentLevelProgressPercent'
  | 'currentExp'
  | 'userTakenItems'
  | 'collected'
  | 'currentLevelQuestLevel'
  | 'eventLevels'
  | 'possibleItems'
  | 'prizeSpinTracks'
  | 'caseItems'
>;

export const eventStateAdapter = (
  currentLevelQuest: ICurrentLevelQuest | null,
  userQuestInfo: ILevelQuestUserInfo | null,
): EventStateAdapterResult => {
  const levelQuestUserLevelsLength = userQuestInfo?.levelQuestUserLevels?.length || 0;
  const currentLevelQuestLevel = currentLevelQuest?.levels?.[levelQuestUserLevelsLength];
  const currentLevelQuestLevelPrepared = {
    id: currentLevelQuestLevel?.id,
    caseId: currentLevelQuestLevel?.reward?.item?.freeCases?.[0]?.case?.id,
  };
  const currentStep = levelQuestUserLevelsLength;

  const userTakenItems = userQuestInfo?.levelQuestUserLevels.map((l) => getItemForHistory(l)) ?? [];
  const eventLevels = currentLevelQuest?.levels?.map((l) => getLevel(l, userQuestInfo, currentStep)) || [];
  const eventLevelsSorted = eventLevels.sort((a, b) => a.levelId - b.levelId);
  const nextLevelRequiredExp = getNextLevelRequiredExp(userTakenItems, eventLevelsSorted);
  const currentExp = Math.round(userQuestInfo?.exp ?? 0);
  const currentLevelProgressPercent = getCurrentLevelProgressPercent(currentExp, nextLevelRequiredExp);
  const collected = getCollected(userTakenItems);
  const endAt = currentLevelQuest?.endAt || '';

  return {
    endAt,
    currentStep,
    nextLevelRequiredExp,
    currentLevelProgressPercent,
    currentExp,
    userTakenItems,
    collected,
    currentLevelQuestLevel: currentLevelQuestLevelPrepared,
    eventLevels: eventLevelsSorted,
    possibleItems: [],
    prizeSpinTracks: [],
    caseItems: {},
  };
};

const getCollected = (userTakenItems: ILastItem[]): number => {
  const price = userTakenItems?.reduce((total, item) => total + item.cost, 0);
  return Math.round((price ?? 0) * 100) / 100;
};

export const getCurrentLevelProgressPercent = (currentExp: number, nextLevelRequiredExp: number): number => {
  if (nextLevelRequiredExp === 0) {
    return 0;
  }
  const progress = (currentExp / nextLevelRequiredExp) * 100;
  return Math.min(Math.round(progress), 100);
};

const getNextLevelRequiredExp = (userTakenItems: ILastItem[], eventLevels: IEventLevel[]): number => {
  const lastUserTakenLevel = userTakenItems.at(-1);
  const lastTakenLevelId = lastUserTakenLevel?.levelId ?? 0;

  if (!eventLevels || eventLevels.length === 0) {
    return 0;
  }

  const nextEventLevel = eventLevels.find((l) => l.levelId === lastTakenLevelId + 1);
  return nextEventLevel?.experience ?? 0;
};

const getItemForHistory = (level: ILevelQuestUserLevel): ILastItem => {
  const rewardResult = level?.rewardResults?.[0];
  const inventoryItem = rewardResult?.userInventoryItem?.inventoryItem;
  const image = transformItemImgUrl(inventoryItem?.baseItem?.icon || '').replace('webp', 'png') || '';

  return {
    id: inventoryItem?.id || 0,
    levelId: level?.levelQuestLevel?.type === 'all-levels' ? FINAL_LEVEL_ID : level.levelQuestLevel.level,
    cellId: level.levelQuestLevel.level - 1,
    image: image || '',
    icon: inventoryItem?.baseItem?.icon || '',
    cost: (inventoryItem?.price || 0) / 100,
    type: inventoryItem?.baseItem?.skin || '',
    replayId: rewardResult?.openUUID,
    caseId: rewardResult?.case?.id,
    experience: level?.levelQuestLevel?.exp || 0,
    rarity: inventoryItem?.baseItem?.rarity || '',
    color: inventoryItem?.baseItem?.color || '',
  };
};

const getLevelStatus = (
  isTaken: boolean,
  userExp: number,
  levelExp: number,
  currentStep: number,
  levelNumber: number,
): EAprilMadnessEventLevelStatus => {
  if (isTaken) {
    return EAprilMadnessEventLevelStatus.TAKEN;
  }

  const isAvailable = userExp >= levelExp && currentStep + 1 === levelNumber;
  return isAvailable ? EAprilMadnessEventLevelStatus.AVAILABLE : EAprilMadnessEventLevelStatus.DISABLED;
};

const getCaseType = (levelNumber: number): ECaseType => {
  const caseTypes = [
    [0],
    [1, 2, 3, 4, 5],
    [6, 7, 8, 9, 10],
    [11, 12, 13, 14, 15],
    [16, 17, 18, 19, 20],
    [21, 22, 23, 24, 25],
    [26, 27, 28, 29, 30],
    [FINAL_LEVEL_ID],
  ];
  return caseTypes.findIndex((t) => t.includes(levelNumber)) as ECaseType;
};

// eslint-disable-next-line complexity
const getLevel = (level: ICurrentLevelQuestLevel, userQuestInfo: ILevelQuestUserInfo | null, currentStep: number): IEventLevel => {
  const item = userQuestInfo?.levelQuestUserLevels?.find((l) => l.levelQuestLevel.id === level.id);
  const levelNumber = level.type === 'all-levels' ? FINAL_LEVEL_ID : level.level;
  const userQuestInfoExp = userQuestInfo?.exp ?? 0;

  const inventoryItem = item?.rewardResults?.[0]?.userInventoryItem?.inventoryItem;
  const caseItem = level?.reward?.item?.freeCases?.[0]?.case;
  const baseItem = inventoryItem?.baseItem;
  const itemImage = transformItemImgUrl(baseItem?.icon || '').replace('webp', 'png');

  return {
    caseType: getCaseType(levelNumber),
    levelId: levelNumber,
    levelBackendId: level?.id || 0,
    status: getLevelStatus(!!item, userQuestInfoExp, level.exp, currentStep, levelNumber),
    experience: level?.exp || 0,
    caseId: caseItem?.id || 0,
    item: {
      image: itemImage || '',
      id: item?.id || 0,
      cost: (inventoryItem?.price || 0) / 100,
      shortName: baseItem?.shortName || '',
      skin: baseItem?.skin || '',
      rarity: baseItem?.rarity || '',
      color: baseItem?.color || '',
      type: baseItem?.type || '',
      exterior: baseItem?.exterior || '',
    },
    case: {
      id: caseItem?.id || 0,
      name: caseItem?.name || '',
      image: caseItem?.historyImage || caseItem?.image || caseItem?.backgroundImage || '',
      exp: level?.exp || 0,
    },
  };
};

export const levelQuestUserLevelUpdateAdapter = (
  data: ILevelQuestUserLevelUpdate,
  currentState: Pick<AprilMadnessEventModel, 'userTakenItems' | 'collected' | 'eventLevels' | 'currentExp'>,
): Partial<AprilMadnessEventModel> => {
  const { userTakenItems, collected, eventLevels, currentExp } = currentState;

  const rewardResult = data.rewardResults?.[0];
  const inventoryItem = rewardResult?.userInventoryItem?.inventoryItem;
  const image = transformItemImgUrl(inventoryItem?.baseItem?.icon || '').replace('webp', 'png') || '';

  const levelId = data?.levelQuestLevel?.type === 'all-levels' ? FINAL_LEVEL_ID : data.levelQuestLevel.level;

  const newItem: ILastItem = {
    id: inventoryItem?.id || 0,
    levelId: levelId,
    cellId: levelId - 1,
    image: image,
    icon: inventoryItem?.baseItem?.icon || '',
    cost: (inventoryItem?.price || 0) / 100,
    type: inventoryItem?.baseItem?.skin || '',
    replayId: rewardResult?.openUUID,
    caseId: rewardResult?.case?.id,
    experience: data.levelQuestLevel?.exp || 0,
    rarity: inventoryItem?.baseItem?.rarity || '',
    color: inventoryItem?.baseItem?.color || '',
    shortName: inventoryItem?.baseItem?.shortName || '',
    skin: inventoryItem?.baseItem?.skin || '',
    exterior: inventoryItem?.baseItem?.exterior || '',
  };

  const updatedUserTakenItems = [...userTakenItems, newItem];
  const updatedCollected = Math.round((collected + newItem.cost) * 100) / 100;
  const currentStep = levelId;

  const updatedEventLevels = eventLevels.map((level) => {
    if (level.levelId === levelId) {
      return {
        ...level,
        status: EAprilMadnessEventLevelStatus.TAKEN,
        item: {
          ...level.item,
          image: newItem.image,
          id: newItem.id,
          cost: newItem.cost,
          shortName: newItem?.shortName || '',
          skin: newItem?.skin || '',
          rarity: newItem.rarity || '',
          color: newItem.color || '',
          type: newItem.type || '',
          exterior: newItem?.exterior || '',
        },
      };
    }

    if (level.levelId === levelId + 1 && currentExp >= level.experience) {
      return {
        ...level,
        status: EAprilMadnessEventLevelStatus.AVAILABLE,
      };
    }
    return level;
  });

  const nextLevel = eventLevels.find((l) => l.levelId === levelId + 1);
  const nextLevelRequiredExp = nextLevel?.experience ?? 0;
  const currentLevelQuestLevel = {
    id: nextLevel?.levelId,
    caseId: nextLevel?.caseId,
  };
  const currentLevelProgressPercent = getCurrentLevelProgressPercent(currentExp, nextLevelRequiredExp);

  return {
    currentStep,
    userTakenItems: updatedUserTakenItems,
    collected: updatedCollected,
    eventLevels: updatedEventLevels,
    nextLevelRequiredExp: nextLevelRequiredExp,
    currentLevelProgressPercent,
    currentLevelQuestLevel,
  };
};
